import { useState, useRef, useEffect } from 'react';
import { PEITEPAKSUUS_RANGES } from '../../../../../utils/measurementUtils';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';

// Pure UI, duplicated from the original PeitepaksuusBlock.jsx (same
// constants, same table/chart layout). No API calls in here — everything
// comes in as props from the PeitepaksuusBlockV2 controller, which gets its
// data from usePeitepaksuusV2 (service). karb-side editing has been removed
// on purpose: those values are backend-derived now, this view only displays
// them (see the "Johdettu" badge).

const YEARS_AHEAD = [10, 20, 30, 40, 50];

function fmtNum(v, d = 1) {
  if (v === null || v === undefined) return '—';
  return typeof v === 'number' ? (Number.isInteger(v) ? String(v) : v.toFixed(d)) : String(v);
}
function fmtPct(v) {
  if (!v && v !== 0) return '—';
  return (v * 100).toFixed(1) + ' %';
}

const C    = { border: '1px solid #adb5bd', padding: '7px 7px', fontSize: '0.8rem', verticalAlign: 'middle' };
const CH   = { ...C, background: '#04aa00', fontWeight: 700, textAlign: 'center', fontSize: '0.75rem', color: '#fff' };
const CL   = { ...C, background: '#f8f9fa', fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const CR   = { ...C, textAlign: 'right', whiteSpace: 'nowrap' };
const CLbl = { ...C, background: '#04aa00', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', color: '#fff' };
const inputSt = { border: '1px solid #ced4da', borderRadius: 3, padding: '2px 6px', fontSize: '0.8rem', background: '#fff', width: '100%' };

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

function ScatterChart({ coverArr, carbonStats }) {
  if (!coverArr.length) return null;
  const W = 560, H = 200;
  const ml = 42, mr = 115, mt = 10, mb = 34;
  const cW = W - ml - mr;
  const cH = H - mt - mb;
  const rawMax = Math.max(...coverArr, carbonStats.max ?? 0);
  const maxY = Math.ceil(rawMax / 10) * 10 + 10;
  const xScale = i => ml + (coverArr.length > 1 ? (i / (coverArr.length - 1)) * cW : cW / 2);
  const yScale = v => mt + cH - (v / maxY) * cH;
  const yTicks = Array.from({ length: Math.floor(maxY / 10) + 1 }, (_, i) => i * 10);
  const step = Math.max(1, Math.ceil(coverArr.length / 6));
  const xTicks = Array.from({ length: Math.floor((coverArr.length - 1) / step) + 1 }, (_, i) => i * step)
    .concat(coverArr.length - 1)
    .filter((v, i, a) => a.indexOf(v) === i && v < coverArr.length)
    .sort((a, b) => a - b);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {yTicks.map(v => <line key={v} x1={ml} y1={yScale(v)} x2={ml + cW} y2={yScale(v)} stroke="#e0e0e0" strokeWidth={0.5} />)}
      <line x1={ml} y1={mt} x2={ml} y2={mt + cH} stroke="#666" strokeWidth={1} />
      <line x1={ml} y1={mt + cH} x2={ml + cW} y2={mt + cH} stroke="#666" strokeWidth={1} />
      {yTicks.map(v => (
        <g key={v}>
          <line x1={ml - 3} y1={yScale(v)} x2={ml} y2={yScale(v)} stroke="#666" strokeWidth={1} />
          <text x={ml - 5} y={yScale(v)} textAnchor="end" dominantBaseline="middle" fontSize={8} fill="#555">{v}</text>
        </g>
      ))}
      <text transform={`translate(10,${mt + cH / 2}) rotate(-90)`} textAnchor="middle" fontSize={8} fill="#555">Etäisyys [mm]</text>
      {xTicks.map(i => (
        <g key={i}>
          <line x1={xScale(i)} y1={mt + cH} x2={xScale(i)} y2={mt + cH + 3} stroke="#666" strokeWidth={1} />
          <text x={xScale(i)} y={mt + cH + 12} textAnchor="middle" fontSize={8} fill="#555">{i + 1}</text>
        </g>
      ))}
      <text x={ml + cW / 2} y={H - 3} textAnchor="middle" fontSize={8} fill="#555">Mittausten lukumäärä [kpl]</text>
      {carbonStats.max !== null && <line x1={ml} y1={yScale(carbonStats.max)} x2={ml + cW} y2={yScale(carbonStats.max)} stroke="#e74c3c" strokeWidth={1.5} />}
      {carbonStats.ka  !== null && <line x1={ml} y1={yScale(carbonStats.ka)}  x2={ml + cW} y2={yScale(carbonStats.ka)}  stroke="#2980b9" strokeWidth={1.5} strokeDasharray="6 3" />}
      {carbonStats.min !== null && <line x1={ml} y1={yScale(carbonStats.min)} x2={ml + cW} y2={yScale(carbonStats.min)} stroke="#27ae60" strokeWidth={1.5} strokeDasharray="2 3" />}
      {coverArr.map((v, i) => <circle key={i} cx={xScale(i)} cy={yScale(v)} r={2.5} fill="#888" fillOpacity={0.85} />)}
      <g transform={`translate(${ml + cW + 10},${mt + 4})`}>
        <circle cx={5} cy={6} r={3} fill="#888" />
        <text x={12} y={10} fontSize={8} fill="#333">Peitepaksuus</text>
        {carbonStats.max !== null && <><line x1={0} y1={22} x2={10} y2={22} stroke="#e74c3c" strokeWidth={1.5} /><text x={12} y={26} fontSize={8} fill="#333">Karb. max</text></>}
        {carbonStats.ka  !== null && <><line x1={0} y1={38} x2={10} y2={38} stroke="#2980b9" strokeWidth={1.5} strokeDasharray="4 2" /><text x={12} y={42} fontSize={8} fill="#333">Karb. ka</text></>}
        {carbonStats.min !== null && <><line x1={0} y1={54} x2={10} y2={54} stroke="#27ae60" strokeWidth={1.5} strokeDasharray="2 2" /><text x={12} y={58} fontSize={8} fill="#333">Karb. min</text></>}
      </g>
    </svg>
  );
}

let mtKeySeq = 0;
const genMtKey = () => `mt_${++mtKeySeq}`;

// Editable (cover mm) or read-only (karb mm, backend-derived) measurement
// table. Editable `rows` are [{id, value, _localKey}] — id is the row's
// real peitepaksuus_arvo id (null for the not-yet-saved trailing slot,
// which usePeitepaksuusV2's refresh() always keeps present); _localKey is a
// permanent per-row client identity (see usePeitepaksuusV2's comment) used
// as the React key instead of id/index.
//
// Inputs are uncontrolled (defaultValue + onBlur/Enter), exactly like
// Poränäyte's fields, and for the same reason: a refetch after saving
// replaces `rows` wholesale, and a *controlled* input immediately echoes
// whatever's in that new array back into its own value — any timing overlap
// between that and the user still typing reads as flicker, or worse, wipes
// out what they just typed. Poränäyte never hits this because every one of
// its rows already has a permanent DB id and key the moment it exists; this
// table's "always show a growing empty row to type into" slot doesn't, so
// it gets its own stable _localKey instead, keeping the input mounted (and
// therefore untouched by any refetch) across its whole lifetime — including
// the moment it actually gets saved and its `id` changes from null to real.
//
// Saving a row PATCHes/POSTs/DELETEs just that one row (onPatchRow/
// onCreateRow/onRemoveRow) instead of replacing the whole cover-measurement
// list on every edit — two rows saved close together used to each send a
// full "destroy everything, recreate everything" request, and whichever
// wrote last on the server silently won, erasing whatever the other, newer
// edit had just added. Per-row writes can't race like that.
// tunnusByRow: readOnly tables only — maps row index to its source
// näytetunnus, shown in the "#" column instead of a bare row index.
function MeasurementTable({ label, rows, setRows, onPatchRow, onCreateRow, onRemoveRow, onPasteRows, readOnly, tunnusByRow, saving }) {
  const isMobile = useIsMobile();
  const inputRefs = useRef({});
  // Guards against firing onCreateRow (a POST — not idempotent, unlike
  // PATCH/DELETE) twice for the same not-yet-saved row. Belt-and-braces: the
  // Enter handler below no longer double-triggers commitRow, but a POST
  // should never rely on "only one caller will ever invoke this" as its only
  // safety — see feedback_saving_ui_principles memory.
  const creatingRef = useRef(new Set());

  const addRow = () => setRows(prev => [...prev, { id: null, value: '', _localKey: genMtKey() }]);
  const removeRow = (i) => {
    const row = rows[i];
    setRows(prev => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length ? next : [{ id: null, value: '', _localKey: genMtKey() }];
    });
    if (row.id != null) onRemoveRow(row.id);
  };
  // Reads the input's own live DOM value — called on blur AND on Enter (so
  // fast keyboard entry still auto-grows the table), not on every keystroke
  // (that's what "uncontrolled" means: no onChange writing to React state
  // at all, only this).
  // Never awaits the save before letting the user keep typing: a new empty
  // row is appended immediately (optimistic), and the real id gets patched
  // into it — matched by its permanent _localKey, not by object identity or
  // index, since the optimistic update below already replaces the object —
  // once the create actually resolves, in the background. Previously this
  // awaited the POST (and its follow-up refetch) before the next row even
  // existed in the DOM, so typing was blocked for however long that
  // round-trip took.
  const commitRow = (i) => {
    const row = rows[i];
    const key = row._localKey;
    const el = inputRefs.current[i];
    const val = el ? el.value : row.value;
    const isValid = !isNaN(parseFloat(String(val).replace(',', '.'))) && val !== '';
    if (row.id != null) {
      if (isValid) onPatchRow(row.id, val);
      else onRemoveRow(row.id); // cleared out an existing value -> delete the row
      return;
    }
    if (!isValid) return;
    if (creatingRef.current.has(key)) return; // already creating this row — ignore a duplicate trigger
    creatingRef.current.add(key);
    setRows(prev => {
      const patched = prev.map(r => r._localKey === key ? { ...r, value: val } : r);
      const otherPending = patched.some(r => r.id == null && r._localKey !== key);
      return otherPending ? patched : [...patched, { id: null, value: '', _localKey: genMtKey() }];
    });
    onCreateRow(val)
      .then(created => {
        if (created?.id == null) return;
        setRows(prev => prev.map(r => r._localKey === key ? { ...r, id: created.id, value: val } : r));
      })
      .finally(() => creatingRef.current.delete(key));
  };
  const handleKeyDown = (e, i) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur (not a direct commitRow call) — blurring synchronously fires
      // the input's own onBlur={() => commitRow(i)} exactly once. Calling
      // commitRow directly here too used to double-fire it: the .focus()
      // below (moving to the next row) forces the browser to blur *this*
      // input first as a side effect, which re-ran commitRow a second time
      // for the same row — harmless for PATCH, but for a not-yet-saved row
      // it fired two POSTs and created two identical DB rows.
      e.target.blur();
      setTimeout(() => {
        const next = inputRefs.current[i + 1];
        if (next) { next.focus(); next.select(); }
      }, 0);
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      // Explicit blur() first, same reasoning as Enter above — and load-
      // bearing here for a reason Enter didn't have: when Tab is pressed on
      // the newest (trailing) row, it IS the topIdx row below, so
      // target.focus() would target the already-focused element. Browsers
      // no-op focus() on an element that already has focus — no blur fires
      // — so without this, a brand-new value typed into the last row and
      // then Tab-ed away from was never committed at all (silently lost).
      e.target.blur();
      setTimeout(() => {
        const indices = Object.keys(inputRefs.current).map(Number).filter(k => inputRefs.current[k]);
        if (!indices.length) return;
        const topIdx = Math.max(...indices);
        const target = inputRefs.current[topIdx];
        if (target) { target.focus(); target.select(); }
      }, 0);
    }
  };
  const handlePaste = (e, rowIndex) => {
    const text = e.clipboardData.getData('text');
    const parts = text.trim().split(/[\s,;\n\t]+/).filter(s => s);
    if (parts.length > 1) {
      e.preventDefault();
      const values = rows.map(r => r.value);
      parts.forEach((val, i) => {
        if (rowIndex + i < values.length) values[rowIndex + i] = val;
        else values.push(val);
      });
      const filtered = values.filter(v => String(v).trim() !== '');
      // Optimistic: show the pasted result immediately instead of freezing
      // the table until the bulk save round-trips (it destroys and
      // recreates every row, so every id changes regardless). Those rows
      // remount once anyway when the real save lands and refresh() picks up
      // the new ids — a small one-time hiccup, far better than the table
      // sitting frozen for the whole request.
      setRows([
        ...filtered.map(v => ({ id: null, value: v, _localKey: genMtKey() })),
        { id: null, value: '', _localKey: genMtKey() },
      ]);
      onPasteRows(filtered);
    }
  };
  const validCount = rows.filter(r => !isNaN(parseFloat(String(r.value).replace(',', '.'))) && String(r.value).trim() !== '').length;

  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      {!readOnly && <style>{`
        .pp-mm-input:focus { outline: none; border-color: #0d6efd !important; box-shadow: 0 0 0 2px rgba(13,110,253,.2); }
        @keyframes pp-spin { to { transform: rotate(360deg); } }
      `}</style>}
      <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        {label} — {validCount} kpl
        {readOnly && <span style={{ background: '#198754', color: '#fff', fontSize: '0.55rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', borderRadius: 4, padding: '1px 6px' }}>Johdettu</span>}
        {saving && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#0d6efd', fontWeight: 600 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', border: '1.5px solid #0d6efd', borderTopColor: 'transparent', animation: 'pp-spin 0.6s linear infinite' }} />
            Tallennetaan…
          </span>
        )}
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <colgroup>
          <col style={{ width: 28 }} />
          <col style={isMobile ? {} : { width: 56 }} />
          {!isMobile && PEITEPAKSUUS_RANGES.map((_, i) => <col key={i} style={{ width: 40 }} />)}
          {!readOnly && <col style={{ width: 22 }} />}
        </colgroup>
        <thead>
          <tr>
            <th style={{ ...CH, padding: '3px 4px' }}>#</th>
            <th style={CH}>mm</th>
            {!isMobile && PEITEPAKSUUS_RANGES.map(r => <th key={r.label} style={{ ...CH, fontSize: '0.62rem', padding: '3px 2px' }}>{r.label}</th>)}
            {!readOnly && <th style={{ ...CH, padding: 2 }} />}
          </tr>
        </thead>
        <tbody>
          {!readOnly && rows.every(r => String(r.value).trim() !== '') && (
            <tr>
              <td colSpan={isMobile ? 3 : 13} style={{ ...C, background: '#f8f9fa', padding: '3px 8px' }}>
                <button type="button" onClick={addRow} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#0d6efd', fontSize: '0.75rem', fontWeight: 600 }}>+ Lisää rivi</button>
              </td>
            </tr>
          )}
          {[...rows].reverse().map((row, displayIdx) => {
            const i = rows.length - 1 - displayIdx;
            const num = parseFloat(String(row.value).replace(',', '.'));
            const valid = !isNaN(num) && num > 0;
            const rangeIdx = valid ? PEITEPAKSUUS_RANGES.findIndex(r => r.test(num)) : -1;
            if (readOnly && !valid) return null;
            return (
              <tr key={readOnly ? i : row._localKey}>
                <td style={{ ...C, textAlign: 'center', color: '#adb5bd', fontSize: '0.68rem', padding: '2px 3px' }}>
                  {valid ? (tunnusByRow ? (tunnusByRow[i] ?? '—') : displayIdx) : ''}
                </td>
                <td style={{ ...C, padding: '2px 4px', textAlign: readOnly ? 'center' : undefined }}>
                  {readOnly ? (
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#155724' }}>{row.value}</span>
                  ) : (
                    <input ref={el => { inputRefs.current[i] = el; }} type="number" min={0} defaultValue={row.value}
                      onBlur={() => commitRow(i)}
                      onPaste={e => handlePaste(e, i)}
                      onKeyDown={e => handleKeyDown(e, i)}
                      className="pp-mm-input"
                      style={{ border: '1px solid #ced4da', borderRadius: 2, padding: isMobile ? '3px 5px' : '3px 6px', fontSize: '0.88rem', width: isMobile ? '100%' : 62, textAlign: 'right', caretColor: '#212529' }}
                      placeholder="mm" />
                  )}
                </td>
                {!isMobile && PEITEPAKSUUS_RANGES.map((_, ri) => (
                  <td key={ri} style={{ ...C, textAlign: 'center', padding: '2px 3px', fontSize: '0.75rem', background: ri === rangeIdx ? '#d4edda' : undefined, fontWeight: ri === rangeIdx ? 700 : undefined, color: ri === rangeIdx ? '#155724' : '#dee2e6' }}>
                    {ri === rangeIdx ? '1' : ''}
                  </td>
                ))}
                {!readOnly && (
                  <td style={{ ...C, textAlign: 'center', padding: 2 }}>
                    <button type="button" onClick={() => removeRow(i)} title="Poista"
                      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#adb5bd', fontSize: isMobile ? '1.1rem' : '0.9rem', lineHeight: 1 }}>×</button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function renderResultsTable({ coverArr, carbArr, stats, dist, age, coeff, projection }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <colgroup>{PEITEPAKSUUS_RANGES.map((_, i) => <col key={i} style={{ width: 58 }} />)}<col style={{ width: 80 }} /></colgroup>
        <thead>
          <tr>
            <th style={CH}>Syvyysalue (mm)</th>
            {PEITEPAKSUUS_RANGES.map(r => <th key={r.label} style={CH}>{r.label}</th>)}
            <th style={CH}>YHT</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={CL}>Osuus peitepaksuushavainnoista</td>
            {dist.cover.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)}
            <td style={{ ...CR, fontWeight: 700 }}>{coverArr.length ? '100.0 %' : '—'}</td>
          </tr>
          <tr>
            <td style={CL}>Osuus karbonatisoitumissyvyyshavainnoista</td>
            {dist.carbon.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)}
            <td style={{ ...CR, fontWeight: 700 }}>{carbArr.length ? '100.0 %' : '—'}</td>
          </tr>
          <tr>
            <td style={CL}>Korroosiotilassa olevan raudoituksen kokonaismäärä</td>
            {dist.corroded
              ? dist.corroded.map((b, i) => <td key={i} style={CR}>{b.pct > 0 ? fmtPct(b.pct) : ''}</td>)
              : PEITEPAKSUUS_RANGES.map((_, i) => <td key={i} style={{ ...CR, color: '#adb5bd' }}>—</td>)}
            <td style={{ ...CR, fontWeight: 700, color: dist.corrodedTotalPct ? '#c0392b' : undefined }}>
              {dist.corroded ? fmtPct(dist.corrodedTotalPct) : '—'}
            </td>
          </tr>
          <tr><td colSpan={12} style={{ padding: 3, border: 'none' }} /></tr>
          <tr>
            <td style={CLbl}>Betonipeitemittaukset</td>
            <td style={CLbl} colSpan={2}>KESKIARVO</td>
            <td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(stats.cover.ka)}</td>
            <td style={CLbl}>MIN</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(stats.cover.min, 0)}</td>
            <td style={CLbl}>MAX</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(stats.cover.max, 0)}</td>
            <td style={CLbl}>KHAJ</td><td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(stats.cover.khaj)}</td>
          </tr>
          <tr>
            <td style={CLbl}>Karbonatisoitumismittaukset</td>
            <td style={CLbl} colSpan={2}>KESKIARVO</td>
            <td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(stats.carbon.ka)}</td>
            <td style={CLbl}>MIN</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(stats.carbon.min, 0)}</td>
            <td style={CLbl}>MAX</td><td style={{ ...CR, fontWeight: 700 }}>{fmtNum(stats.carbon.max, 0)}</td>
            <td style={CLbl}>KHAJ</td><td style={{ ...CR, fontWeight: 700 }} colSpan={2}>{fmtNum(stats.carbon.khaj)}</td>
          </tr>
          <tr><td colSpan={12} style={{ padding: 3, border: 'none' }} /></tr>
          <tr>
            <td style={CLbl}>Tutkimusajankohdasta</td>
            {YEARS_AHEAD.map(y => <td key={y} style={CH}>{y} v</td>)}
            <td colSpan={2} style={{ ...C, background: '#f8f9fa' }} />
            <td style={CLbl}>Rakenteen ikä (a)</td>
            <td style={CR}>{age ?? '—'}</td>
            <td style={CLbl}>Karb. kerroin</td>
            <td style={{ ...CR, fontWeight: 700 }}>{coeff ? coeff.toFixed(2) : '—'}</td>
          </tr>
          <tr>
            <td style={CL}>Karbonatisoitumisen eteneminen (mm)</td>
            {projection
              ? YEARS_AHEAD.map(y => <td key={y} style={{ ...CR, fontWeight: 600 }}>{projection[y].toFixed(1)}</td>)
              : YEARS_AHEAD.map((_, i) => <td key={i} style={{ ...CR, color: '#adb5bd' }}>—</td>)}
            <td colSpan={6} style={{ ...C, background: '#fff', fontSize: '0.7rem', color: '#6c757d' }}>
              {coeff ? `k = KESKIARVO / √ikä = ${fmtNum(stats.carbon.ka)} / √${age} = ${coeff.toFixed(4)}, eteneminen = k × √(ikä + v)` : ''}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PeitepaksuusView({ peite, coverRows, setCoverRows, patchCoverRow, createCoverRow, removeCoverRow, replaceCoverRows, saving, loading, ageInput, setAgeInput, commitAge, caption, commitCaption, chartCaption, commitChartCaption, taulukkoNum = 1, error }) {
  const isMobile = useIsMobile();
  const [showInputs, setShowInputs] = useState(true);

  // No peite yet (still loading, or failed to load) — this view is only
  // ever mounted once a rakenneosa is selected, so show the spinner in
  // place of the table rather than rendering nothing.
  if (!peite) return <div style={{ position: 'relative', minHeight: 120 }}><TableLoadingOverlay show={loading} /></div>;

  const coverArr = coverRows.map(r => parseFloat(String(r.value).replace(',', '.'))).filter(v => !isNaN(v) && v > 0);
  const carbArr = [...peite.karbonatisaatio.ylapinta, ...peite.karbonatisaatio.alapinta];
  const carbRows = carbArr.map(v => ({ id: null, value: String(v) }));
  const carbEntries = [...(peite.karbonatisaatio.ylapintaEntries ?? []), ...(peite.karbonatisaatio.alapintaEntries ?? [])];
  const carbTunnusByRow = carbEntries.length === carbRows.length ? carbEntries.map(e => e.tunnus) : undefined;

  return (
    <div style={{ fontFamily: "'Lato', sans-serif", position: 'relative' }}>
      <TableLoadingOverlay show={loading} />
      {error && (
        <div style={{ fontSize: '0.78rem', color: '#842029', background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '0 0 80px' }}>
          <div style={{ fontSize: '0.7rem', color: '#6c757d', fontWeight: 600, marginBottom: 2 }}>IKÄ (a)</div>
          <input style={{ ...inputSt, color: ageInput ? '#000' : '#6c757d' }} type="number" min={0} value={ageInput}
            onChange={e => setAgeInput(e.target.value)}
            onBlur={() => commitAge(ageInput)}
            placeholder={peite.auto_age ? String(peite.auto_age) : 'vuotta'} />
        </div>
        <button type="button" onClick={() => setShowInputs(v => !v)}
          style={{ fontSize: '0.75rem', padding: '3px 10px', border: '1px solid #ced4da', borderRadius: 3, background: showInputs ? '#e9ecef' : '#fff', cursor: 'pointer', alignSelf: 'flex-end' }}>
          {showInputs ? 'Laskut ja kuvaajat' : 'Mittausdata'}
        </button>
      </div>

      {showInputs ? (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <MeasurementTable label="BETONIPEITEMITTAUKSET (mm)" rows={coverRows} setRows={setCoverRows}
            onPatchRow={patchCoverRow} onCreateRow={createCoverRow} onRemoveRow={removeCoverRow} onPasteRows={replaceCoverRows} saving={saving} />
          <MeasurementTable label="KARBONATISOITUMISSYVYYSMITTAUKSET (mm)" rows={carbRows.length ? carbRows : [{ id: null, value: '' }]} readOnly tunnusByRow={carbTunnusByRow} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#495057', marginBottom: 4, padding: '3px 6px', background: '#f8f9fa', borderRadius: 3, display: 'inline-block' }}>
            TULOKSET{coverArr.length > 0 && <span style={{ fontWeight: 400, marginLeft: 6, color: '#6c757d' }}>{coverArr.length} kpl</span>}
          </div>
          {renderResultsTable({ coverArr, carbArr, stats: peite.stats, dist: peite.dist, age: peite.age, coeff: peite.coeff, projection: peite.projection })}
        </>
      )}

      {!isMobile && !showInputs && coverArr.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ border: '1px solid #dee2e6', borderRadius: 3, padding: '6px 4px 2px' }}>
            <ScatterChart coverArr={coverArr} carbonStats={peite.stats.carbon} />
          </div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginTop: 6, color: '#212529' }}>
            Mitattujen terästen peitepaksuudet ja betonin karbonatisoitumissyvyyden vaihteluväli.
          </div>
        </div>
      )}

      {/* kohdeId is keyed off peite.id (the already-loaded row), not rakId
          (the selector) — rakId changes synchronously the instant a
          different rakenneosa is picked, before the new peite has actually
          been fetched, so keying off it remounted the editor early and
          locked it onto the still-stale caption text (MinimalLexical only
          hydrates once per mount). Keying off peite.id defers the remount
          until the real data has arrived. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={peite.id ?? 'preview'} caption={caption} onChange={commitCaption} label="taulukko" />
      <TaulukkoFooter taulukkoNum={taulukkoNum + 1} kohdeId={(peite.id ?? 'preview') + '-chart'} caption={chartCaption} onChange={commitChartCaption} label="kuvaaja" />

      <div style={{ background: '#f0fdf4', border: '1px solid #b7e4c7', borderRadius: 8, padding: '8px 12px', fontSize: '0.7rem', color: '#155724', marginTop: 10 }}>
        {peite.karbonatisaatio._note}
      </div>
    </div>
  );
}
